import { Builder_, Dropdown_, Filled_Edit, FormBox, FormBoxSelection, FormBoxTitle, Input_, Main_Plain, Slider_, Toggle_Stat } from 'monica-alexandria';
import L from 'leaflet';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import styled from 'styled-components';
import { Calendar_Field } from '../../../../components/form/Calendar_Field';
import Timeline_ from '../../../../components/timeline/Timeline_';
import {
    read_sass_paid_requirements_message,
    read_sass_paid_selection_requirements
} from '../utils/sass_paid_requirements';
import {
    read_sass_outcome_request_payload,
    read_sass_outcome_submits_directly
} from '../utils/sass_outcome_payload';
import {
    sass_username_matches_typed_prefix,
    sass_username_match_is_current
} from '../utils/sass_username_match';
import {
    read_sass_edit_permissions,
    read_sass_slider_primary_action
} from '../utils/sass_edit_permissions';
import { read_active_sass_session_event } from '../utils/sass_timeline_events';

const SassSliderContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--normalPads);

    .Builder{
        grid-template-columns: 1fr 1fr;
    }
`;

const SassOutcomeSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
`;

const SassAdBusinessFields = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--normalPads);
`;

const SassAdBusinessField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const SassAdBusinessToggle = styled(Toggle_Stat)`
    ${p => p.disabled ? `
        pointer-events: none;
        opacity: 0.65;
    ` : ''}
`;

const SassOutcomeError = styled.div`
    color: var(--error);

    p {
        margin: 0;
    }
`;

const SassOutcomeSuccess = styled.div`
    color: var(--success);

    p {
        margin: 0;
    }
`;

const SassBrandSliderSelected = styled.div`
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    align-items: center;
`

const SassEditableFormBox = styled(FormBox)`
    cursor: pointer;
    pointer-events: ${p => p.locked ? 'none' : 'auto'};
`;

const SassEditableFormBoxTitle = styled(FormBoxTitle)`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
`;

const SassEditableFormBoxTitleText = styled.div`
    display: flex;
    align-items: center;
    gap: 0.6rem;
`;

const SassLocationEditor = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const SassLocationResults = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
`;

const SassLocationResult = styled.button`
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    width: 100%;
    padding: var(--normalPads);
    border: none;
    border-radius: var(--normalRadius);
    color: ${p => p.theme.color};
    background: ${p => p.theme.low};
    text-align: left;
    cursor: pointer;

    h5,
    h6 {
        margin: 0;
        overflow-wrap: anywhere;
    }

    h6 {
        color: ${p => p.theme.high};
    }
`;

const SassLocationMapFrame = styled.div`
    height: 24rem;
    overflow: hidden;
    border-radius: var(--normalRadius);
    box-shadow: ${p => p.theme.in};
    background: ${p => p.theme.low};

    .leaflet-container {
        width: 100%;
        height: 100%;
    }
`;

const QuillWrap = styled.div`
    .ql-toolbar.ql-snow {
        border: none;
        background: ${p => p.theme.background};
        box-shadow: ${p => p.theme.in};
        border-radius: var(--smallRadius) var(--smallRadius) 0 0;
        padding: var(--normalPads);
    }

    .ql-container.ql-snow {
        border: none;
        background: ${p => p.theme.background};
        box-shadow: ${p => p.theme.in};
        border-radius: 0 0 var(--smallRadius) var(--smallRadius);
    }

    .ql-editor {
        min-height: 14rem;
        color: ${p => p.theme.color};
        font-size: var(--normalText);
    }

    .ql-editor.ql-blank::before {
        color: ${p => p.theme.color};
        opacity: 0.5;
        font-style: normal;
    }

    .ql-snow .ql-stroke {
        stroke: ${p => p.theme.color};
    }

    .ql-snow .ql-fill {
        fill: ${p => p.theme.color};
    }

    .ql-snow .ql-picker {
        color: ${p => p.theme.color};
    }

    .ql-toolbar.ql-snow .ql-picker.ql-expanded .ql-picker-options {
        background: ${p => p.theme.background};
        border: none;
        box-shadow: ${p => p.theme.out};
    }

    .ql-toolbar.ql-snow + .ql-container.ql-snow {
        border-top: none;
    }
`;

const SassCommentSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
`;

const SassCommentComposer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean']
    ]
};

const quillFormats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'link'
];

const sassOutcomeRescheduled = 'rescheduled';
const sassOutcomePaid = 'paid';
const sassOutcomeLost = 'lost';
const sassAdProviderMeta = 'meta';
const sassAdProviderGoogle = 'google';
const sassAdProviders = [sassAdProviderMeta, sassAdProviderGoogle];

const defaultLocationPosition = {
    lat: 37.98381,
    lng: 23.72754
};

const sassLocationMarkerIcon = L.icon({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const sass_comment_plain_text = (value = '') => {
    return `${value || ''}`
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
};

const read_future_datetime = (value = '') => {
    const dateValue = new Date(value || '');

    if (Number.isNaN(dateValue.getTime())) {
        return '';
    }

    if (dateValue.getTime() <= Date.now()) {
        return '';
    }

    return dateValue.toISOString();
};

const read_clean_username = (value = '') => {
    return `${value || ''}`.trim().toLowerCase();
};

const read_internal_brand_name = (value = '') => {
    return `${value || ''}`.trim().toUpperCase().replace(/\s+/g, ' ');
};

const read_email_username = (value = '') => {
    const username = read_clean_username(value);

    return username.includes('@') ? username : '';
};

const username_has_domain_dot = (value = '') => {
    const username = read_clean_username(value);
    const atIndex = username.indexOf('@');
    const domainText = atIndex >= 0 ? username.slice(atIndex + 1) : '';

    return atIndex > 0 && domainText.indexOf('.') > 0;
};

const read_registered_user_payload = (value = {}) => {
    return {
        userId: `${value?.userId || value?.sub || value?.id || ''}`.trim(),
        username: read_clean_username(value?.username || value?.preferred_username || value?.email),
        firstname: `${value?.firstname || value?.firstName || value?.given_name || ''}`.trim(),
        lastname: `${value?.lastname || value?.lastName || value?.family_name || ''}`.trim(),
        emails: clean_editable_list_values([
            ...(Array.isArray(value?.emails) ? value.emails : []),
            ...(Array.isArray(value?.emailAddresses) ? value.emailAddresses : []),
            value?.email
        ])
    };
};

const empty_username_match = {
    status: 'idle',
    typedUsername: '',
    registeredUser: null
};

const empty_edit_state = {
    brandName: false,
    username: false,
    selectedBusiness: false,
    firstName: false,
    lastName: false,
    phoneNumbers: false,
    emailAddresses: false,
    category: false,
    subcategory: false,
    location: false
};

const editable_list_values = (values = []) => {
    const nextValues = (values || []).filter((value) => `${value || ''}`.trim().length);

    if (nextValues.length) {
        return nextValues;
    }

    return [''];
};

const clean_editable_list_values = (values = []) => {
    const seenValues = new Set();

    return (values || []).map((value) => `${value || ''}`.trim()).filter((value) => {
        const valueKey = value.toLowerCase();
        const keepValue = value.length && !seenValues.has(valueKey);

        if (keepValue) {
            seenValues.add(valueKey);
        }

        return keepValue;
    });
};

const read_assigned_caller_label = (assignedCallerOptions = [], assignedCallerValue = '', fallbackLabel = '') => {
    const selectedOption = (assignedCallerOptions || []).find((optionItem) => optionItem.optionValue === assignedCallerValue);

    if (selectedOption) {
        return selectedOption.optionLabel || selectedOption.name || selectedOption.username || selectedOption.email || assignedCallerValue;
    }

    return fallbackLabel || assignedCallerValue || '-';
};

const read_assigned_caller_options = (assignedCallerOptions = [], assignedCallerValue = '', fallbackLabel = '') => {
    const hasSelectedOption = (assignedCallerOptions || []).some((optionItem) => optionItem.optionValue === assignedCallerValue);

    if (hasSelectedOption || !assignedCallerValue.length) {
        return assignedCallerOptions || [];
    }

    return [
        {
            optionValue: assignedCallerValue,
            optionLabel: fallbackLabel || assignedCallerValue
        },
        ...(assignedCallerOptions || [])
    ];
};

const read_selected_assigned_caller = (assignedCallerOptions = [], assignedCallerValue = '', fallbackLabel = '') => {
    if (!`${assignedCallerValue || ''}`.trim().length) {
        return {};
    }

    const selectedOption = (assignedCallerOptions || []).find((optionItem) => optionItem.optionValue === assignedCallerValue);

    if (selectedOption) {
        return selectedOption;
    }

    return {
        optionValue: assignedCallerValue,
        optionLabel: fallbackLabel || assignedCallerValue
    };
};

const read_business_option_value = (optionItem = {}) => {
    return `${optionItem?.value || optionItem?.optionValue || optionItem?.businessId || optionItem?._id || optionItem?.id || ''}`.trim();
};

const read_business_option_label = (optionItem = {}, fallbackLabel = '') => {
    return `${optionItem?.label || optionItem?.optionLabel || optionItem?.businessName || optionItem?.name || fallbackLabel || ''}`.trim();
};

const read_business_option_provider = (optionItem = {}) => {
    const assetProvider = (optionItem?.assets || []).find((asset) => `${asset?.provider || ''}`.trim().length)?.provider;
    const provider = `${optionItem?.provider || optionItem?.metadata?.provider || assetProvider || ''}`.trim().toLowerCase().replace(/[\s-]+/g, '_');
    const identityKey = `${optionItem?.identityKey || optionItem?.metadata?.identityKey || ''}`.trim().toLowerCase();

    if (['google', 'google_ads'].includes(provider)
        || identityKey.startsWith('google:')
        || `${optionItem?.googleCustomerId || optionItem?.metadata?.googleCustomerId || ''}`.trim().length) {
        return sassAdProviderGoogle;
    }

    return sassAdProviderMeta;
};

const business_option_is_placeholder = (optionItem = {}) => {
    const optionValue = read_business_option_value(optionItem).toLowerCase();
    const optionLabel = read_business_option_label(optionItem).toLowerCase();

    return ['default', 'no-business', 'no business'].includes(optionValue)
        || ['default', 'no-business', 'no business'].includes(optionLabel);
};

const read_business_options_from_response = (response = {}) => {
    const sourceOptions = response?.options || response?.businesses || [];

    return (sourceOptions || []).map((optionItem) => {
        const optionValue = read_business_option_value(optionItem);
        const optionLabel = read_business_option_label(optionItem, optionValue);

        return {
            ...optionItem,
            value: optionValue,
            label: optionLabel,
            optionValue,
            optionLabel,
            provider: read_business_option_provider(optionItem)
        };
    }).filter((optionItem) => optionItem.value.length && !business_option_is_placeholder(optionItem));
};

const read_provider_business_options = (options = [], provider = '') => {
    return options.filter((optionItem) => read_business_option_provider(optionItem) === provider);
};

const read_dropdown_business_options = (options = [], placeholderLabel = 'Select a business') => {
    return [
        {
            value: 'no-business',
            label: placeholderLabel,
            optionValue: 'no-business',
            optionLabel: placeholderLabel
        },
        ...options
    ];
};

const read_selected_business_payload = (options = [], selectedValue = '', fallbackLabel = '') => {
    const selectedOption = options.find((optionItem) => read_business_option_value(optionItem) === selectedValue) || {};
    const businessId = business_option_is_placeholder(selectedOption) ? '' : read_business_option_value(selectedOption);
    const businessName = read_business_option_label(selectedOption, fallbackLabel);

    if (!businessId.length) {
        return {};
    }

    return {
        ...selectedOption,
        value: businessId,
        label: businessName,
        businessId,
        businessName
    };
};

const read_account_selected_ad_businesses = (accountData = {}) => {
    const savedSelections = Array.isArray(accountData.selectedAdBusinesses)
        ? accountData.selectedAdBusinesses
        : [];

    if (savedSelections.length) {
        return savedSelections.map((business) => {
            return {
                ...business,
                provider: read_business_option_provider(business)
            };
        }).filter((business) => !business_option_is_placeholder(business));
    }

    const selectedBusinessValue = `${accountData.selectedBusinessValue || ''}`.trim();

    if (!selectedBusinessValue.length || ['default', 'no-business'].includes(selectedBusinessValue.toLowerCase())) {
        return [];
    }

    return [{
        value: selectedBusinessValue,
        label: accountData.selectedBusinessLabel || selectedBusinessValue,
        provider: accountData.selectedBusinessProvider || sassAdProviderMeta
    }];
};

const read_selected_ad_providers = (providerInput = {}, selectionOrder = []) => {
    return [
        ...selectionOrder.filter((provider) => providerInput[provider]),
        ...sassAdProviders.filter((provider) => providerInput[provider] && !selectionOrder.includes(provider))
    ];
};

const read_selected_ad_businesses = (options = [], selectedInputs = {}, selectionOrder = []) => {
    return selectionOrder.map((provider) => {
        const selectedValue = selectedInputs[provider] || 'no-business';

        return read_selected_business_payload(
            read_provider_business_options(options, provider),
            selectedValue,
            selectedValue
        );
    }).filter((business) => Object.keys(business).length);
};

const read_account_user_id_from_offer = (offerItem = {}) => {
    return `${offerItem?.niffler?.client?.accountUserId || offerItem?.niffler?.client?.userId || offerItem?.client?.accountUserId || offerItem?.client?.userId || offerItem?.typesData?.sourceClientUserId || ''}`.trim();
};

const read_sass_source_identity_from_offer = (offerItem = {}) => {
    return {
        sourceBrandId: `${offerItem?.sourceBrandId || offerItem?.niffler?.sourceBrandId || offerItem?.typesData?.sourceBrandId || ''}`.trim(),
        sourceProspectId: `${offerItem?.sourceProspectId || offerItem?.niffler?.sourceProspectId || offerItem?.typesData?.sourceProspectId || ''}`.trim(),
        sourceLeadId: `${offerItem?.sourceLeadId || offerItem?.niffler?.sourceLeadId || offerItem?.typesData?.sourceLeadId || ''}`.trim()
    };
};

const read_option_values = (values = [], selectedValue = '') => {
    const cleanValues = values.map((value) => `${value || ''}`.trim()).filter((value) => value.length);
    const selectedOption = `${selectedValue || ''}`.trim();

    if (selectedOption.length && !cleanValues.includes(selectedOption)) {
        return [selectedOption, ...cleanValues];
    }

    return cleanValues;
};

const read_category_options = (categoryMap = {}, selectedCategory = '') => {
    return read_option_values(Object.keys(categoryMap || {}), selectedCategory);
};

const read_subcategory_options = (categoryMap = {}, selectedCategory = '', selectedSubcategory = '') => {
    return read_option_values(categoryMap?.[selectedCategory] || [], selectedSubcategory);
};

const read_latlng_is_valid = (latlng) => {
    return Number.isFinite(latlng?.lat) && Number.isFinite(latlng?.lng);
};

const read_location_search_url = (value = '') => {
    const query = encodeURIComponent(value.trim());

    return `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=5&q=${query}`;
};

const read_search_result = (result = {}) => {
    return {
        id: result.place_id,
        label: result.display_name,
        type: result.type,
        latlng: {
            lat: Number(result.lat),
            lng: Number(result.lon)
        }
    };
};

const read_location_coordinate_label = (latlng) => {
    return `${latlng.lat.toFixed(6)}, ${latlng.lng.toFixed(6)}`;
};

const SassLocationMap = (props) => {
    const mapElementRef = useRef(null);
    const mapRef = useRef(null);
    const markerRef = useRef(null);

    useEffect(() => {
        if (!mapElementRef.current || mapRef.current) {
            return;
        }

        mapRef.current = L.map(mapElementRef.current).setView([props.position.lat, props.position.lng], props.zoom);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors'
        }).addTo(mapRef.current);

        mapRef.current.on('click', (event) => {
            props.onPick({
                lat: event.latlng.lat,
                lng: event.latlng.lng
            });
        });

        window.setTimeout(() => {
            mapRef.current?.invalidateSize();
        }, 0);

        return () => {
            mapRef.current?.remove();
            mapRef.current = null;
            markerRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!mapRef.current || !read_latlng_is_valid(props.position)) {
            return;
        }

        const nextPosition = [props.position.lat, props.position.lng];

        mapRef.current.setView(nextPosition, props.zoom);

        if (!markerRef.current) {
            markerRef.current = L.marker(nextPosition, {
                icon: sassLocationMarkerIcon
            }).addTo(mapRef.current);
            return;
        }

        markerRef.current.setLatLng(nextPosition);
    }, [props.position.lat, props.position.lng, props.zoom]);

    return (
        <SassLocationMapFrame>
            <div ref={mapElementRef}/>
        </SassLocationMapFrame>
    );
};

const SassEditTitle = (props) => {
    return (
        <SassEditableFormBoxTitle>
            <SassEditableFormBoxTitleText>
                {props.editable && !props.active ? <Filled_Edit/> : null}
                <h4>{props.title}</h4>
            </SassEditableFormBoxTitleText>
        </SassEditableFormBoxTitle>
    );
};

export default function Sass_Brand_Slider(props) {
    const offerItem = props.offerItem;
    const accountData = props.accountData || {};
    const assignedCallerOptions = props.assignedCallerOptions || [];
    const categoryMap = props.categoryMap || {};
    const [commentsInput, setCommentsInput] = useState('');
    const [isSavingComment, setIsSavingComment] = useState(false);
    const [outcomeInput, setOutcomeInput] = useState('');
    const [outcomeDateInput, setOutcomeDateInput] = useState('');
    const [outcomeCommentInput, setOutcomeCommentInput] = useState('');
    const [outcomeError, setOutcomeError] = useState('');
    const [isSavingOutcome, setIsSavingOutcome] = useState(false);
    const [isCancellingProgress, setIsCancellingProgress] = useState(false);
    const [accountSaveError, setAccountSaveError] = useState('');
    const [assignedCallerSaveError, setAssignedCallerSaveError] = useState('');
    const [brandNameInput, setBrandNameInput] = useState('');
    const [accountUsernameInput, setAccountUsernameInput] = useState('');
    const [accountUsernameMatch, setAccountUsernameMatch] = useState(empty_username_match);
    const [accountUserIdInput, setAccountUserIdInput] = useState('');
    const [selectedAdProviderInput, setSelectedAdProviderInput] = useState({
        [sassAdProviderMeta]: false,
        [sassAdProviderGoogle]: false
    });
    const [selectedAdBusinessInput, setSelectedAdBusinessInput] = useState({
        [sassAdProviderMeta]: 'no-business',
        [sassAdProviderGoogle]: 'no-business'
    });
    const [selectedAdBusinessOrder, setSelectedAdBusinessOrder] = useState([]);
    const [savedBusinessState, setSavedBusiness] = useState(null);
    const [businessOptions, setBusinessOptions] = useState([]);
    const [isLoadingBusinessOptions, setIsLoadingBusinessOptions] = useState(false);
    const [businessOptionsError, setBusinessOptionsError] = useState('');
    const [edit, setEdit] = useState(empty_edit_state);
    const [assignedCallerInput, setAssignedCallerInput] = useState('');
    const [firstNameInput, setFirstNameInput] = useState('');
    const [lastNameInput, setLastNameInput] = useState('');
    const [phoneNumberValue, setPhoneNumberValue] = useState('');
    const [phoneNumbersInput, setPhoneNumbersInput] = useState(['']);
    const [emailAddressValue, setEmailAddressValue] = useState('');
    const [emailAddressesInput, setEmailAddressesInput] = useState(['']);
    const [categoryInput, setCategoryInput] = useState('');
    const [subcategoryInput, setSubcategoryInput] = useState('');
    const [locationInput, setLocationInput] = useState('');
    const [locationResults, setLocationResults] = useState([]);
    const [locationPosition, setLocationPosition] = useState(defaultLocationPosition);
    const selectedLocationRef = useRef('');
    const businessSelectionTouchedRef = useRef(false);
    const offerIdentity = `${offerItem?._id || offerItem?.ofrId || ''}`.trim();
    const savedBusiness = savedBusinessState?.offerIdentity === offerIdentity ? savedBusinessState.business : null;

    useEffect(() => {
        setCommentsInput('');
        setIsSavingComment(false);
        setOutcomeInput('');
        setOutcomeDateInput('');
        setOutcomeCommentInput('');
        setOutcomeError('');
        setIsSavingOutcome(false);
        setIsCancellingProgress(false);
        setAccountSaveError('');
        setAssignedCallerSaveError('');
        setEdit(empty_edit_state);
        setBrandNameInput(
            read_internal_brand_name(accountData.internalName)
            || read_internal_brand_name(accountData.brandName)
        );
        setAccountUsernameInput(accountData.accountUsername ?? accountData.username ?? '');
        setAccountUsernameMatch(empty_username_match);
        setAccountUserIdInput(accountData.accountUserId || '');
        const savedAdBusinesses = read_account_selected_ad_businesses(accountData);
        const savedAdBusinessOrder = [...new Set(savedAdBusinesses.map((business) => read_business_option_provider(business)))];
        setSelectedAdProviderInput({
            [sassAdProviderMeta]: savedAdBusinessOrder.includes(sassAdProviderMeta),
            [sassAdProviderGoogle]: savedAdBusinessOrder.includes(sassAdProviderGoogle)
        });
        setSelectedAdBusinessInput({
            [sassAdProviderMeta]: read_business_option_value(savedAdBusinesses.find((business) => read_business_option_provider(business) === sassAdProviderMeta)) || 'no-business',
            [sassAdProviderGoogle]: read_business_option_value(savedAdBusinesses.find((business) => read_business_option_provider(business) === sassAdProviderGoogle)) || 'no-business'
        });
        setSelectedAdBusinessOrder(savedAdBusinessOrder);
        setSavedBusiness(null);
        setBusinessOptions([]);
        setIsLoadingBusinessOptions(false);
        setBusinessOptionsError('');
        businessSelectionTouchedRef.current = false;
        setAssignedCallerInput(accountData.assignedCallerValue || accountData.assignedCaller || '');
        setFirstNameInput(accountData.firstName || '');
        setLastNameInput(accountData.lastName || '');
        setPhoneNumberValue('');
        setPhoneNumbersInput(editable_list_values(accountData.phoneNumbers));
        setEmailAddressValue('');
        setEmailAddressesInput(editable_list_values(accountData.emailAddresses));
        setCategoryInput(accountData.category || '');
        setSubcategoryInput(accountData.subcategory || '');
        setLocationInput(accountData.location || '');
        setLocationResults([]);
        setLocationPosition(defaultLocationPosition);
        selectedLocationRef.current = accountData.location || '';
    }, [offerIdentity]);

    useEffect(() => {
        if (!accountData.accountUserId) {
            return;
        }

        setAccountUserIdInput(accountData.accountUserId);
    }, [accountData.accountUserId]);

    useEffect(() => {
        if (businessSelectionTouchedRef.current) {
            return;
        }

        const savedAdBusinesses = read_account_selected_ad_businesses(accountData);
        const savedAdBusinessOrder = [...new Set(savedAdBusinesses.map((business) => read_business_option_provider(business)))];
        setSelectedAdProviderInput({
            [sassAdProviderMeta]: savedAdBusinessOrder.includes(sassAdProviderMeta),
            [sassAdProviderGoogle]: savedAdBusinessOrder.includes(sassAdProviderGoogle)
        });
        setSelectedAdBusinessInput({
            [sassAdProviderMeta]: read_business_option_value(savedAdBusinesses.find((business) => read_business_option_provider(business) === sassAdProviderMeta)) || 'no-business',
            [sassAdProviderGoogle]: read_business_option_value(savedAdBusinesses.find((business) => read_business_option_provider(business) === sassAdProviderGoogle)) || 'no-business'
        });
        setSelectedAdBusinessOrder(savedAdBusinessOrder);
    }, [accountData.selectedBusinessValue, accountData.selectedAdBusinessesKey]);

    useEffect(() => {
        const usernameValue = read_clean_username(accountUsernameInput);

        if (!props.inProgress || !usernameValue.length) {
            setAccountUsernameMatch(empty_username_match);
            return;
        }

        if (!username_has_domain_dot(usernameValue) || !props.onMatchAccountUsername) {
            setAccountUsernameMatch({
                status: 'idle',
                typedUsername: usernameValue,
                registeredUser: null
            });
            return;
        }

        let requestActive = true;
        const timeoutId = window.setTimeout(() => {
            setAccountUsernameMatch({
                status: 'loading',
                typedUsername: usernameValue,
                registeredUser: null
            });

            props.onMatchAccountUsername(usernameValue)
                .then((response) => {
                    if (!requestActive) {
                        return;
                    }

                    const registeredUser = read_registered_user_payload(response?.user || {});

                    if (response?.matched === true
                        && Number(response?.count || 0) === 1
                        && registeredUser.userId
                        && sass_username_matches_typed_prefix(registeredUser.username, usernameValue)) {
                        setAccountUsernameMatch({
                            status: 'matched',
                            typedUsername: usernameValue,
                            registeredUser
                        });
                        setAccountUserIdInput(registeredUser.userId);

                        if (registeredUser.firstname.length) {
                            setFirstNameInput(registeredUser.firstname);
                        }

                        if (registeredUser.lastname.length) {
                            setLastNameInput(registeredUser.lastname);
                        }

                        return;
                    }

                    setAccountUsernameMatch({
                        status: 'unmatched',
                        typedUsername: usernameValue,
                        registeredUser: null
                    });
                })
                .catch(() => {
                    if (!requestActive) {
                        return;
                    }

                    setAccountUsernameMatch({
                        status: 'unmatched',
                        typedUsername: usernameValue,
                        registeredUser: null
                    });
                });
        }, 450);

        return () => {
            requestActive = false;
            window.clearTimeout(timeoutId);
        };
    }, [accountUsernameInput, props.inProgress, props.onMatchAccountUsername]);

    useEffect(() => {
        if (!props.inProgress || !offerItem || !props.onLoadBusinessOptions || !accountUserIdInput.length) {
            setBusinessOptions([]);
            setBusinessOptionsError('');
            setIsLoadingBusinessOptions(false);
            return;
        }

        let requestActive = true;

        setIsLoadingBusinessOptions(true);
        setBusinessOptionsError('');

        props.onLoadBusinessOptions(offerItem, {
            accountUsername: accountUsernameInput,
            accountUserId: accountUserIdInput,
            brandName: brandNameInput
        })
            .then((response) => {
                if (!requestActive) {
                    return;
                }

                const nextOptions = read_business_options_from_response(response);

                setBusinessOptions(nextOptions);
                if (response.businessLocked && response.selectedBusiness && !business_option_is_placeholder(response.selectedBusiness)) {
                    const savedProvider = read_business_option_provider(response.selectedBusiness);
                    setSavedBusiness({ offerIdentity, business: { ...response.selectedBusiness, provider: savedProvider } });
                    setAccountUserIdInput(response.userId || accountUserIdInput);
                    if (response.username) {
                        setAccountUsernameInput(response.username);
                    }
                    if (!businessSelectionTouchedRef.current) {
                        setSelectedAdProviderInput((currentProviders) => {
                            return {
                                ...currentProviders,
                                [savedProvider]: true
                            };
                        });
                        setSelectedAdBusinessInput((currentSelections) => {
                            return {
                                ...currentSelections,
                                [savedProvider]: read_business_option_value(response.selectedBusiness)
                            };
                        });
                        setSelectedAdBusinessOrder((currentOrder) => {
                            return currentOrder.includes(savedProvider) ? currentOrder : [...currentOrder, savedProvider];
                        });
                    }
                }
                if (!response.businessLocked) {
                    setSavedBusiness(null);
                }
            })
            .catch(() => {
                if (!requestActive) {
                    return;
                }

                setBusinessOptions([]);
                setBusinessOptionsError(props.t('Could not load businesses.'));
            })
            .finally(() => {
                if (requestActive) {
                    setIsLoadingBusinessOptions(false);
                }
            });

        return () => {
            requestActive = false;
        };
    }, [offerIdentity, props.inProgress, accountUserIdInput, props.onLoadBusinessOptions]);

    useEffect(() => {
        if (!edit.location) {
            setLocationResults([]);
            return;
        }

        const cleanLocationInput = `${locationInput || ''}`.trim();

        if (!cleanLocationInput || cleanLocationInput === selectedLocationRef.current) {
            setLocationResults([]);
            return;
        }

        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => {
            fetch(read_location_search_url(cleanLocationInput), {
                signal: controller.signal
            })
                .then((response) => response.json())
                .then((results) => setLocationResults((results || []).map(read_search_result).filter((result) => read_latlng_is_valid(result.latlng))))
                .catch(() => setLocationResults([]));
        }, 350);

        return () => {
            controller.abort();
            window.clearTimeout(timeoutId);
        };
    }, [edit.location, locationInput]);

    if (!offerItem) {
        return null;
    }

    const commentHasText = Boolean(sass_comment_plain_text(commentsInput));
    const availableAssignedCallerOptions = read_assigned_caller_options(assignedCallerOptions, assignedCallerInput, accountData.assignedCaller);
    const assignedCallerLabel = read_assigned_caller_label(availableAssignedCallerOptions, assignedCallerInput, accountData.assignedCaller);
    const selectedAssignedCaller = read_selected_assigned_caller(availableAssignedCallerOptions, assignedCallerInput, accountData.assignedCaller);
    const categoryOptions = read_category_options(categoryMap, categoryInput);
    const subcategoryOptions = read_subcategory_options(categoryMap, categoryInput, subcategoryInput);
    const metaBusinessDropdownOptions = read_dropdown_business_options(
        read_provider_business_options(businessOptions, sassAdProviderMeta),
        props.t(isLoadingBusinessOptions ? 'Loading businesses' : 'Select a business')
    );
    const googleBusinessDropdownOptions = read_dropdown_business_options(
        read_provider_business_options(businessOptions, sassAdProviderGoogle),
        props.t(isLoadingBusinessOptions ? 'Loading businesses' : 'Select a business')
    );
    const selectedAdProviders = read_selected_ad_providers(selectedAdProviderInput, selectedAdBusinessOrder);
    const selectedAdBusinesses = read_selected_ad_businesses(
        businessOptions,
        selectedAdBusinessInput,
        selectedAdBusinessOrder
    );
    const showOutcomeDate = outcomeInput === sassOutcomeRescheduled;
    const showOutcomeComment = outcomeInput === sassOutcomeLost;
    const editPermissions = read_sass_edit_permissions(props.boardStatus, props.canManageAssignments);
    const sliderActionText = editPermissions.viewOnly ? props.t('Done') : props.t('Save');
    const canEditAssignedCaller = editPermissions.canEditAssignee;
    const canEditAccount = editPermissions.canEditAccount;
    const paidRequirements = read_sass_paid_selection_requirements(
        sass_username_match_is_current(accountUsernameMatch, accountUsernameInput),
        selectedAdProviders,
        selectedAdBusinesses
    );
    const showPaidAdBusinessFields = outcomeInput === sassOutcomePaid
        && sass_username_match_is_current(accountUsernameMatch, accountUsernameInput);
    const savedBusinessProvider = read_business_option_provider(savedBusiness || {});

    const open_edit_field = (fieldName = '') => {
        if (!canEditAccount || edit[fieldName]) {
            return;
        }
        if (savedBusiness && fieldName === 'username') {
            return;
        }

        setEdit((currentEdit) => {
            return {
                ...currentEdit,
                [fieldName]: true
            };
        });
    };

    const read_account_payload = () => {
        const typedUsername = read_clean_username(accountUsernameInput);
        const hasMatchedUser = sass_username_match_is_current(accountUsernameMatch, typedUsername);
        const registeredUser = hasMatchedUser ? accountUsernameMatch.registeredUser : null;
        const accountUsername = registeredUser?.username || typedUsername;
        const accountUserId = registeredUser?.userId || `${accountUserIdInput || ''}`.trim();
        const firstName = registeredUser?.firstname || `${firstNameInput || ''}`.trim();
        const lastName = registeredUser?.lastname || `${lastNameInput || ''}`.trim();
        const internalName = read_internal_brand_name(brandNameInput);

        const accountPayload = {
            ...read_sass_source_identity_from_offer(offerItem),
            brandName: internalName,
            internalName,
            accountUsername,
            accountUsernameTyped: typedUsername,
            accountUserId,
            firstName,
            lastName,
            phoneNumbers: clean_editable_list_values([
                ...(phoneNumbersInput || []),
                phoneNumberValue
            ]),
            emailAddresses: clean_editable_list_values([
                ...(emailAddressesInput || []),
                emailAddressValue,
                ...(registeredUser?.emails || []),
                read_email_username(registeredUser?.username)
            ]),
            category: `${categoryInput || ''}`.trim(),
            subcategory: `${subcategoryInput || ''}`.trim(),
            location: `${locationInput || ''}`.trim(),
            registeredUser: registeredUser || {}
        };

        if (outcomeInput === sassOutcomePaid || businessSelectionTouchedRef.current) {
            return {
                ...accountPayload,
                selectedAdProviders,
                selectedAdBusinesses,
                selectedBusiness: selectedAdBusinesses[0] || {}
            };
        }

        return accountPayload;
    };

    const read_account_save_error = () => {
        const usernameValue = read_clean_username(accountUsernameInput);

        if (!usernameValue.length) {
            return '';
        }

        if (!username_has_domain_dot(usernameValue)) {
            return props.t('Account username must be an email.');
        }

        if (!sass_username_match_is_current(accountUsernameMatch, usernameValue)) {
            return props.t('User must be matched before saving.');
        }

        return '';
    };

    const read_outcome_payload = () => {
        const outcomePayload = {
            outcome: outcomeInput,
            rescheduledAt: read_future_datetime(outcomeDateInput),
            comment: outcomeCommentInput,
            accountUsername: `${accountUsernameInput || ''}`.trim(),
            assignedCaller: selectedAssignedCaller
        };

        return read_sass_outcome_request_payload(outcomePayload, read_account_payload());
    };

    const click_slider_action = () => {
        const primaryAction = read_sass_slider_primary_action(props.boardStatus, outcomeInput);

        if (primaryAction === 'save_account') {
            save_in_progress_changes();
            return;
        }

        if (primaryAction === 'submit_outcome') {
            submit_outcome();
            return;
        }

        props.onClose?.();
    };

    const close_all_edit_fields = () => {
        setEdit(empty_edit_state);
    };

    const apply_saved_account_offer = (updatedOffer = {}) => {
        const nextAccountUserId = read_account_user_id_from_offer(updatedOffer);

        if (nextAccountUserId.length) {
            setAccountUserIdInput(nextAccountUserId);
        }

        setPhoneNumberValue('');
        setEmailAddressValue('');
    };

    const save_account_data_from_slider = async() => {
        if (!props.onSaveAccountData) {
            return offerItem;
        }

        try {
            const updatedOffer = await props.onSaveAccountData(offerItem, read_account_payload());

            if (updatedOffer?.saveError) {
                setAccountSaveError(props.t(updatedOffer.saveError));
                return null;
            }

            if (updatedOffer) {
                apply_saved_account_offer(updatedOffer);
                return updatedOffer;
            }

            setAccountSaveError(props.t('Could not save account data.'));
            return null;
        } catch (error) {
            setAccountSaveError(props.t(error?.response?.data?.message || error?.response?.data?.msg || error?.message || 'Could not save account data.'));
            return null;
        }
    };

    const save_in_progress_changes = async() => {
        const nextAccountError = read_account_save_error();

        if (nextAccountError.length || isSavingOutcome) {
            setAccountSaveError(nextAccountError);
            return;
        }

        setAccountSaveError('');
        setOutcomeError('');
        setIsSavingOutcome(true);

        try {
            const updatedOffer = await save_account_data_from_slider();

            if (updatedOffer) {
                close_all_edit_fields();
            }
        } finally {
            setIsSavingOutcome(false);
        }
    };

    const change_outcome_input = (event) => {
        const nextOutcome = event.target.value;

        setOutcomeInput(nextOutcome);
        setOutcomeError('');

        if (nextOutcome === sassOutcomePaid && !`${accountUsernameInput || ''}`.trim().length) {
            open_edit_field('username');
        }

    };

    const read_outcome_error = () => {
        if (!props.inProgress) {
            return '';
        }

        if (!outcomeInput.length) {
            return props.t('Select an outcome.');
        }

        if (outcomeInput === sassOutcomeRescheduled && !read_future_datetime(outcomeDateInput).length) {
            return props.t('Select a future date and time.');
        }

        if (outcomeInput === sassOutcomeLost && !sass_comment_plain_text(outcomeCommentInput).length) {
            return props.t('Comment is required.');
        }

        if (outcomeInput === sassOutcomePaid && !`${accountUsernameInput || ''}`.trim().length) {
            return props.t('Username is required.');
        }

        if (outcomeInput === sassOutcomePaid && !username_has_domain_dot(accountUsernameInput)) {
            return props.t('Account username must be an email.');
        }

        if (outcomeInput === sassOutcomePaid && !paidRequirements.valid) {
            return props.t(read_sass_paid_requirements_message(paidRequirements));
        }

        return '';
    };

    const submit_outcome_for_offer = async(nextOfferItem = {}) => {
        if (!props.onSubmitOutcome) {
            setOutcomeError(props.t('Board save failed.'));
            return;
        }

        const updatedOffer = await props.onSubmitOutcome(nextOfferItem, read_outcome_payload());

        if (updatedOffer?.saveError) {
            setOutcomeError(props.t(updatedOffer.saveError));
            return;
        }

        if (!updatedOffer) {
            setOutcomeError(props.t('Board save failed.'));
            return;
        }

        close_all_edit_fields();
    };

    const submit_outcome = async() => {
        const nextAccountError = outcomeInput === sassOutcomeLost ? '' : read_account_save_error();
        const nextOutcomeError = read_outcome_error();

        if (nextAccountError.length || nextOutcomeError.length || isSavingOutcome) {
            setAccountSaveError(nextAccountError);
            setOutcomeError(nextOutcomeError);
            return;
        }

        setAccountSaveError('');
        setOutcomeError('');
        setIsSavingOutcome(true);

        try {
            if (read_sass_outcome_submits_directly(outcomeInput)) {
                await submit_outcome_for_offer(offerItem);
                return;
            }

            const updatedOffer = await save_account_data_from_slider();

            if (!updatedOffer) {
                return;
            }

            await submit_outcome_for_offer(updatedOffer);
        } finally {
            setIsSavingOutcome(false);
        }
    };

    const cancel_progress = async() => {
        if (isSavingOutcome || isCancellingProgress || !props.onCancelProgress) {
            return;
        }

        setOutcomeError('');
        setIsCancellingProgress(true);

        try {
            await props.onCancelProgress(offerItem);
        } finally {
            setIsCancellingProgress(false);
        }
    };

    const add_phone_number = () => {
        const nextPhoneNumber = `${phoneNumberValue || ''}`.trim();

        if (!nextPhoneNumber.length) {
            return;
        }

        setPhoneNumbersInput((currentValues) => {
            const currentPhoneNumbers = (currentValues || []).filter((phoneNumber) => `${phoneNumber || ''}`.trim().length);

            if (currentPhoneNumbers.includes(nextPhoneNumber)) {
                return currentPhoneNumbers;
            }

            return [...currentPhoneNumbers, nextPhoneNumber];
        });
        setPhoneNumberValue('');
    };

    const remove_phone_number = (index = 0) => {
        setPhoneNumbersInput((currentValues) => {
            return (currentValues || []).filter((_, phoneIndex) => phoneIndex !== index);
        });
    };

    const add_email_address = () => {
        const nextEmailAddress = `${emailAddressValue || ''}`.trim().toLowerCase();

        if (!nextEmailAddress.length) {
            return;
        }

        setEmailAddressesInput((currentValues) => {
            const currentEmailAddresses = (currentValues || []).filter((emailAddress) => `${emailAddress || ''}`.trim().length);

            if (currentEmailAddresses.includes(nextEmailAddress)) {
                return currentEmailAddresses;
            }

            return [...currentEmailAddresses, nextEmailAddress];
        });
        setEmailAddressValue('');
    };

    const remove_email_address = (index = 0) => {
        setEmailAddressesInput((currentValues) => {
            return (currentValues || []).filter((_, emailIndex) => emailIndex !== index);
        });
    };

    const change_category_input = (event) => {
        const nextCategory = event.target.value;
        const nextSubcategories = categoryMap?.[nextCategory] || [];

        setCategoryInput(nextCategory);

        if (!nextSubcategories.includes(subcategoryInput)) {
            setSubcategoryInput('');
        }
    };

    const change_location_input = (event) => {
        selectedLocationRef.current = '';
        setLocationInput(event.target.value);
    };

    const select_location_result = (result) => {
        selectedLocationRef.current = result.label;
        setLocationInput(result.label);
        setLocationPosition(result.latlng);
        setLocationResults([]);
    };

    const select_location_position = (latlng) => {
        const coordinateLabel = read_location_coordinate_label(latlng);

        selectedLocationRef.current = coordinateLabel;
        setLocationInput(coordinateLabel);
        setLocationPosition(latlng);
        setLocationResults([]);
    };

    const set_brand_name_from_ad_selection = (nextSelections = {}, nextOrder = []) => {
        const firstSelectedBusiness = read_selected_ad_businesses(
            businessOptions,
            nextSelections,
            nextOrder
        )[0];

        if (firstSelectedBusiness) {
            setBrandNameInput((currentInternalName) => {
                return read_internal_brand_name(currentInternalName)
                    || read_internal_brand_name(read_business_option_label(firstSelectedBusiness));
            });
        }
    };

    const toggle_ad_provider = (provider = '') => {
        if (savedBusiness && savedBusinessProvider === provider) {
            return;
        }

        const nextProviderInput = {
            ...selectedAdProviderInput,
            [provider]: !selectedAdProviderInput[provider]
        };
        const nextBusinessInput = nextProviderInput[provider] ? selectedAdBusinessInput : {
            ...selectedAdBusinessInput,
            [provider]: 'no-business'
        };
        const nextBusinessOrder = nextProviderInput[provider]
            ? selectedAdBusinessOrder
            : selectedAdBusinessOrder.filter((selectedProvider) => selectedProvider !== provider);

        businessSelectionTouchedRef.current = true;
        setSelectedAdProviderInput(nextProviderInput);
        setSelectedAdBusinessInput(nextBusinessInput);
        setSelectedAdBusinessOrder(nextBusinessOrder);
        set_brand_name_from_ad_selection(nextBusinessInput, nextBusinessOrder);
        setOutcomeError('');
    };

    const change_selected_ad_business = (provider = '', event) => {
        const nextValue = event.target.value;
        const nextBusinessInput = {
            ...selectedAdBusinessInput,
            [provider]: nextValue
        };
        const nextBusinessOrder = nextValue === 'no-business'
            ? selectedAdBusinessOrder.filter((selectedProvider) => selectedProvider !== provider)
            : selectedAdBusinessOrder.includes(provider) ? selectedAdBusinessOrder : [...selectedAdBusinessOrder, provider];

        businessSelectionTouchedRef.current = true;
        setSelectedAdBusinessInput(nextBusinessInput);
        setSelectedAdBusinessOrder(nextBusinessOrder);
        set_brand_name_from_ad_selection(nextBusinessInput, nextBusinessOrder);
        setOutcomeError('');
    };

    const change_account_username = (event) => {
        setAccountUsernameInput(event.target.value);
        setAccountUserIdInput('');
        setAccountUsernameMatch(empty_username_match);
        setSelectedAdProviderInput({
            [sassAdProviderMeta]: false,
            [sassAdProviderGoogle]: false
        });
        setSelectedAdBusinessInput({
            [sassAdProviderMeta]: 'no-business',
            [sassAdProviderGoogle]: 'no-business'
        });
        setSelectedAdBusinessOrder([]);
        setSavedBusiness(null);
        businessSelectionTouchedRef.current = false;
    };

    const change_assigned_seller = async(event) => {
        const previousAssignedCaller = assignedCallerInput;
        const nextAssignedCallerValue = event.target.value;
        const nextAssignedCaller = read_selected_assigned_caller(
            availableAssignedCallerOptions,
            nextAssignedCallerValue,
            nextAssignedCallerValue
        );

        if (nextAssignedCallerValue === previousAssignedCaller || isSavingOutcome) {
            return;
        }

        if (!Object.keys(nextAssignedCaller).length || !props.onSaveAssignedCaller) {
            setAssignedCallerSaveError(props.t('Select team member.'));
            return;
        }

        setAssignedCallerInput(nextAssignedCallerValue);
        setAssignedCallerSaveError('');
        setIsSavingOutcome(true);

        try {
            const updatedOffer = await props.onSaveAssignedCaller(offerItem, nextAssignedCaller);

            if (updatedOffer?.saveError) {
                setAssignedCallerInput(previousAssignedCaller);
                setAssignedCallerSaveError(props.t(updatedOffer.saveError));
                return;
            }

            if (!updatedOffer) {
                setAssignedCallerInput(previousAssignedCaller);
                setAssignedCallerSaveError(props.t('Board save failed.'));
            }
        } catch (error) {
            setAssignedCallerInput(previousAssignedCaller);
            setAssignedCallerSaveError(props.t(error?.response?.data?.message || error?.response?.data?.msg || error?.message || 'Board save failed.'));
        } finally {
            setIsSavingOutcome(false);
        }
    };

    const submit_comment = async() => {
        if (!commentHasText || !props.onSubmitComment || isSavingComment) {
            return;
        }

        setIsSavingComment(true);

        try {
            const nextOfferItem = await props.onSubmitComment(offerItem, commentsInput);

            if (nextOfferItem) {
                setCommentsInput('');
            }
        } finally {
            setIsSavingComment(false);
        }
    };

    return (
        <Slider_
            title={props.t('SASS Brand')}
            text={isSavingOutcome ? props.t('Saving') : sliderActionText}
            disabled={isSavingOutcome || (outcomeInput === sassOutcomePaid && !paidRequirements.valid)}
            iconGreyLeft={props.inProgress ? 'Xicon' : null}
            grey={props.inProgress ? isCancellingProgress ? props.t('Cancelling') : props.t('Cancel') : null}
            onGrey={props.inProgress ? cancel_progress : null}
            onClose={props.onClose}
            onClick={click_slider_action}
        >
            <SassSliderContent>
                <Builder_ split last>
                    <FormBox filled>
                        <FormBoxTitle>
                            <h4>{props.t('Seller')}</h4>
                        </FormBoxTitle>
                        {canEditAssignedCaller ? (
                            <Dropdown_>
                                <select
                                    value={assignedCallerInput}
                                    onChange={change_assigned_seller}
                                    disabled={isSavingOutcome}
                                >
                                    <option value="">{props.t('Select team member')}</option>
                                    {availableAssignedCallerOptions.map((memberOption) => (
                                        <option key={memberOption.optionValue} value={memberOption.optionValue}>
                                            {memberOption.optionLabel}
                                        </option>
                                    ))}
                                </select>
                            </Dropdown_>
                        ) : (
                            <FormBoxSelection>
                                <p>{assignedCallerLabel}</p>
                            </FormBoxSelection>
                        )}
                        {assignedCallerSaveError.length ? (
                            <SassOutcomeError>
                                <p>{assignedCallerSaveError}</p>
                            </SassOutcomeError>
                        ) : null}
                    </FormBox>
                    <FormBox filled>
                        <FormBoxTitle>
                            <h4>{props.t('Caller')}</h4>
                        </FormBoxTitle>
                        <FormBoxSelection>
                            <p>{accountData.caller || '-'}</p>
                        </FormBoxSelection>
                    </FormBox>
                </Builder_>
                {editPermissions.canSelectOutcome ? (
                    <FormBox filled>
                        <FormBoxTitle>
                            <h4>{props.t('Status')}</h4>
                        </FormBoxTitle>
                        <SassOutcomeSection>
                            <Dropdown_>
                                <select
                                    value={outcomeInput}
                                    onChange={change_outcome_input}
                                >
                                    <option value="">{props.t('Select status')}</option>
                                    <option value={sassOutcomeRescheduled}>{props.t('RESCHEDULED')}</option>
                                    <option value={sassOutcomePaid}>{props.t('PAID')}</option>
                                    <option value={sassOutcomeLost}>{props.t('LOST')}</option>
                                </select>
                            </Dropdown_>
                            {showPaidAdBusinessFields ? (
                                <SassAdBusinessFields>
                                    <SassAdBusinessField>
                                        <SassAdBusinessToggle
                                            text={props.t('Meta Ads')}
                                            active={selectedAdProviderInput[sassAdProviderMeta]}
                                            disabled={isSavingOutcome || (savedBusiness && savedBusinessProvider === sassAdProviderMeta)}
                                            onClick={() => toggle_ad_provider(sassAdProviderMeta)}
                                        ></SassAdBusinessToggle>
                                        {selectedAdProviderInput[sassAdProviderMeta] ? (
                                            <Dropdown_>
                                                <select
                                                    value={selectedAdBusinessInput[sassAdProviderMeta] || 'no-business'}
                                                    onChange={(event) => change_selected_ad_business(sassAdProviderMeta, event)}
                                                    disabled={isSavingOutcome || isLoadingBusinessOptions || (savedBusiness && savedBusinessProvider === sassAdProviderMeta)}
                                                >
                                                    {metaBusinessDropdownOptions.map((businessOption) => (
                                                        <option key={read_business_option_value(businessOption)} value={read_business_option_value(businessOption)}>
                                                            {read_business_option_label(businessOption, props.t('Select a business'))}
                                                        </option>
                                                    ))}
                                                </select>
                                            </Dropdown_>
                                        ) : null}
                                    </SassAdBusinessField>
                                    <SassAdBusinessField>
                                        <SassAdBusinessToggle
                                            text={props.t('Google Ads')}
                                            active={selectedAdProviderInput[sassAdProviderGoogle]}
                                            disabled={isSavingOutcome || (savedBusiness && savedBusinessProvider === sassAdProviderGoogle)}
                                            onClick={() => toggle_ad_provider(sassAdProviderGoogle)}
                                        ></SassAdBusinessToggle>
                                        {selectedAdProviderInput[sassAdProviderGoogle] ? (
                                            <Dropdown_>
                                                <select
                                                    value={selectedAdBusinessInput[sassAdProviderGoogle] || 'no-business'}
                                                    onChange={(event) => change_selected_ad_business(sassAdProviderGoogle, event)}
                                                    disabled={isSavingOutcome || isLoadingBusinessOptions || (savedBusiness && savedBusinessProvider === sassAdProviderGoogle)}
                                                >
                                                    {googleBusinessDropdownOptions.map((businessOption) => (
                                                        <option key={read_business_option_value(businessOption)} value={read_business_option_value(businessOption)}>
                                                            {read_business_option_label(businessOption, props.t('Select a business'))}
                                                        </option>
                                                    ))}
                                                </select>
                                            </Dropdown_>
                                        ) : null}
                                    </SassAdBusinessField>
                                </SassAdBusinessFields>
                            ) : null}
                            {outcomeInput === sassOutcomePaid && !paidRequirements.valid ? (
                                <SassOutcomeError>
                                    <p>{props.t(read_sass_paid_requirements_message(paidRequirements))}</p>
                                </SassOutcomeError>
                            ) : null}
                            {showOutcomeDate ? (
                                <Calendar_Field
                                    label={props.t('Reschedule date')}
                                    required
                                    selected={outcomeDateInput ? new Date(outcomeDateInput) : null}
                                    defaultDate={outcomeDateInput ? new Date(outcomeDateInput) : null}
                                    onChange={(date) => {
                                        setOutcomeDateInput(date);
                                        setOutcomeError('');
                                    }}
                                    placeholder={props.t('Choose date and time')}
                                    interval={15}
                                    minDate={new Date()}
                                    minHour={8}
                                    maxHour={22}
                                />
                            ) : null}
                            {showOutcomeComment ? (
                                <QuillWrap>
                                    <ReactQuill
                                        theme="snow"
                                        value={outcomeCommentInput}
                                        onChange={(value) => {
                                            setOutcomeCommentInput(value);
                                            setOutcomeError('');
                                        }}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        placeholder={props.t('Comment')}
                                    />
                                </QuillWrap>
                            ) : null}
                            {outcomeError.length ? (
                                <SassOutcomeError>
                                    <p>{outcomeError}</p>
                                </SassOutcomeError>
                            ) : null}
                        </SassOutcomeSection>
                    </FormBox>
                ) : null}
                <Builder_ last>
                    <SassEditableFormBox locked={!canEditAccount} onClickCapture={() => open_edit_field('brandName')}>
                        <SassEditTitle
                            title={props.t('Internal name')}
                            active={edit.brandName}
                            editable={canEditAccount}
                        />
                        {edit.brandName ?
                            <Input_
                                value={brandNameInput}
                                onChange={(event) => setBrandNameInput(event.target.value.toUpperCase())}
                                placeholder={props.t('Internal name')}
                            />
                        :
                            <FormBoxSelection>
                                <p>{brandNameInput || '-'}</p>
                            </FormBoxSelection>
                        }
                    </SassEditableFormBox>
                </Builder_>
                {showPaidAdBusinessFields && businessOptionsError.length ? (
                    <SassOutcomeError>
                        <p>{businessOptionsError}</p>
                    </SassOutcomeError>
                ) : null}
                <Builder_ last>
                    <SassEditableFormBox locked={!canEditAccount || Boolean(savedBusiness)} onClickCapture={() => open_edit_field('username')}>
                        <SassEditTitle
                            title={props.t("Account username")}
                            active={edit.username}
                            editable={canEditAccount && !savedBusiness}
                        />
                        {edit.username && !savedBusiness ?
                            <Input_
                                value={accountUsernameInput}
                                onChange={change_account_username}
                                placeholder="Email username"
                                type="email"
                                required
                            />
                        :
                            <FormBoxSelection>
                                <p>{accountUsernameInput || '-'}</p>
                            </FormBoxSelection>
                        }
                        {sass_username_match_is_current(accountUsernameMatch, accountUsernameInput) ? (
                            <SassOutcomeSuccess>
                                <p>{props.t('User matched')}</p>
                            </SassOutcomeSuccess>
                        ) : null}
                        {accountUsernameMatch.status === 'loading' ? (
                            <FormBoxSelection>
                                <p>{props.t('Checking user')}</p>
                            </FormBoxSelection>
                        ) : null}
                        {accountSaveError.length ? (
                            <SassOutcomeError>
                                <p>{accountSaveError}</p>
                            </SassOutcomeError>
                        ) : null}
                    </SassEditableFormBox>
                </Builder_>
                <Builder_ split last>
                    <SassEditableFormBox locked={!canEditAccount} onClickCapture={() => open_edit_field('firstName')}>
                        <SassEditTitle
                            title="First name"
                            active={edit.firstName}
                            editable={canEditAccount}
                        />
                        {edit.firstName ?
                            <Input_
                                value={firstNameInput}
                                onChange={(event) => setFirstNameInput(event.target.value)}
                                placeholder="First name"
                            />
                        :
                            <FormBoxSelection>
                                <p>{firstNameInput || '-'}</p>
                            </FormBoxSelection>
                        }
                    </SassEditableFormBox>
                    <SassEditableFormBox locked={!canEditAccount} onClickCapture={() => open_edit_field('lastName')}>
                        <SassEditTitle
                            title="Last name"
                            active={edit.lastName}
                            editable={canEditAccount}
                        />
                        {edit.lastName ?
                            <Input_
                                value={lastNameInput}
                                onChange={(event) => setLastNameInput(event.target.value)}
                                placeholder="Last name"
                            />
                        :
                            <FormBoxSelection>
                                <p>{lastNameInput || '-'}</p>
                            </FormBoxSelection>
                        }
                    </SassEditableFormBox>
                </Builder_>
                
                <SassEditableFormBox locked={!canEditAccount} filled={!edit.phoneNumbers} onClickCapture={() => open_edit_field('phoneNumbers')}>
                    <SassEditTitle
                        title={props.t("Phone numbers")}
                        active={edit.phoneNumbers}
                        editable={canEditAccount}
                    />
                    {edit.phoneNumbers ?
                        <Input_
                            value={phoneNumberValue}
                            onChange={(event) => setPhoneNumberValue(event.target.value)}
                            placeholder="Phone number"
                            list={(phoneNumbersInput || []).filter((phoneNumber) => `${phoneNumber || ''}`.trim().length)}
                            add={props.t('Add')}
                            onAdd={add_phone_number}
                            onDelete={remove_phone_number}
                            icon_ListItem="Phone"
                        />
                    :
                        <SassBrandSliderSelected>
                            {(phoneNumbersInput || []).filter((phoneNumber) => `${phoneNumber || ''}`.length).map((phoneNumber) => (
                                <FormBoxSelection key={phoneNumber}>
                                    <p>{phoneNumber}</p>
                                </FormBoxSelection>
                            ))}
                            {!(phoneNumbersInput || []).filter((phoneNumber) => `${phoneNumber || ''}`.length).length ? (
                                <FormBoxSelection>
                                    <p>-</p>
                                </FormBoxSelection>
                            ) : null}
                        </SassBrandSliderSelected>
                    }
                </SassEditableFormBox>
                <SassEditableFormBox locked={!canEditAccount} filled={!edit.emailAddresses} onClickCapture={() => open_edit_field('emailAddresses')}>
                    <SassEditTitle
                        title={props.t("Email addresses")}
                        active={edit.emailAddresses}
                        editable={canEditAccount}
                    />
                    {edit.emailAddresses ?
                        <Input_
                            value={emailAddressValue}
                            onChange={(event) => setEmailAddressValue(event.target.value)}
                            placeholder="Email address"
                            type="email"
                            list={(emailAddressesInput || []).filter((emailAddress) => `${emailAddress || ''}`.trim().length)}
                            add={props.t('Add')}
                            onAdd={add_email_address}
                            onDelete={remove_email_address}
                            icon_ListItem="Email"
                        />
                    :
                        <SassBrandSliderSelected>
                            {(emailAddressesInput || []).filter((emailAddress) => `${emailAddress || ''}`.length).map((emailAddress) => (
                                <FormBoxSelection key={emailAddress}>
                                    <p>{emailAddress}</p>
                                </FormBoxSelection>
                            ))}
                            {!(emailAddressesInput || []).filter((emailAddress) => `${emailAddress || ''}`.length).length ? (
                                <FormBoxSelection>
                                    <p>-</p>
                                </FormBoxSelection>
                            ) : null}
                        </SassBrandSliderSelected>
                    }
                </SassEditableFormBox>
                <Builder_ split last>
                    <SassEditableFormBox locked={!canEditAccount} onClickCapture={() => open_edit_field('category')}>
                        <SassEditTitle
                            title="Category"
                            active={edit.category}
                            editable={canEditAccount}
                        />
                        {edit.category ?
                            <Dropdown_>
                                <select value={categoryInput} onChange={change_category_input}>
                                    <option value="">{props.t('Category')}</option>
                                    {categoryOptions.map((categoryOption) => (
                                        <option key={categoryOption} value={categoryOption}>
                                            {categoryOption}
                                        </option>
                                    ))}
                                </select>
                            </Dropdown_>
                        :
                            <FormBoxSelection>
                                <p>{categoryInput || '-'}</p>
                            </FormBoxSelection>
                        }
                    </SassEditableFormBox>
                    <SassEditableFormBox locked={!canEditAccount} onClickCapture={() => open_edit_field('subcategory')}>
                        <SassEditTitle
                            title="Subcategory"
                            active={edit.subcategory}
                            editable={canEditAccount}
                        />
                        {edit.subcategory ?
                            <Dropdown_>
                                <select
                                    value={subcategoryInput}
                                    onChange={(event) => setSubcategoryInput(event.target.value)}
                                    disabled={!categoryInput.length && !subcategoryOptions.length}
                                >
                                    <option value="">{props.t('Subcategory')}</option>
                                    {subcategoryOptions.map((subcategoryOption) => (
                                        <option key={subcategoryOption} value={subcategoryOption}>
                                            {subcategoryOption}
                                        </option>
                                    ))}
                                </select>
                            </Dropdown_>
                        :
                            <FormBoxSelection>
                                <p>{subcategoryInput || '-'}</p>
                            </FormBoxSelection>
                        }
                    </SassEditableFormBox>
                </Builder_>
                <SassEditableFormBox locked={!canEditAccount} onClickCapture={() => open_edit_field('location')}>
                    <SassEditTitle
                        title="Location"
                        active={edit.location}
                        editable={canEditAccount}
                    />
                    {edit.location ?
                        <SassLocationEditor>
                            <Input_
                                value={locationInput}
                                onChange={change_location_input}
                                placeholder="Search location"
                            />
                            {locationResults.length ? (
                                <SassLocationResults>
                                    {locationResults.map((locationResult, locationResultIndex) => (
                                        <SassLocationResult
                                            key={locationResult.id || locationResultIndex}
                                            type="button"
                                            onClick={() => select_location_result(locationResult)}
                                        >
                                            <h5>{locationResult.label}</h5>
                                            <h6>{locationResult.type}</h6>
                                        </SassLocationResult>
                                    ))}
                                </SassLocationResults>
                            ) : null}
                            <SassLocationMap
                                position={locationPosition}
                                zoom={13}
                                onPick={select_location_position}
                            />
                        </SassLocationEditor>
                    :
                        <FormBoxSelection>
                            <p>{locationInput || '-'}</p>
                        </FormBoxSelection>
                    }
                </SassEditableFormBox>
                <FormBox filled>
                    <FormBoxTitle>
                        <h4>{props.t('Timeline')}</h4>
                    </FormBoxTitle>
                    <SassCommentSection>
                        <Timeline_
                            onRefresh={props.onRefreshTimeline}
                            leadingEvent={read_active_sass_session_event(offerItem)}
                            events={props.timelineEvents}
                            loading={props.timelineLoading}
                            loadingMore={props.timelineLoadingMore}
                            hasMore={props.timelineHasMore}
                            error={props.timelineError}
                            warning={props.timelineWarning}
                            publication={props.timelinePublication}
                            teamMembers={assignedCallerOptions}
                            onLoadMore={props.onLoadMoreTimeline}
                            onRetry={props.onRetryTimeline}
                            t={props.t}
                        />
                        {editPermissions.canEditComments && !showOutcomeComment ? (
                            <SassCommentComposer>
                                <QuillWrap>
                                    <ReactQuill
                                        theme="snow"
                                        value={commentsInput}
                                        onChange={(value) => setCommentsInput(value)}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        placeholder={props.t('Leave a comment')}
                                    />
                                </QuillWrap>
                                <Main_Plain text={isSavingComment ? props.t('Saving') : props.t('Comment')} iconLeft="Add" disabled={!commentHasText || isSavingComment} onClick={submit_comment} />
                            </SassCommentComposer>
                        ) : null}
                    </SassCommentSection>
                </FormBox>
            </SassSliderContent>
        </Slider_>
    );
}

export { Sass_Brand_Slider };
